import { Accordion, Button, Group, Stack, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { zod4Resolver } from "mantine-form-zod-resolver";
import type { JSX } from "react";
import { useMemo } from "react";
import { z } from "zod/v4";

import InfoNode from "../../InfoNode";
import type { IParsedInput } from "./ImportInputForm";
import classes from "./ImportSelectForm.module.css";

const schema = z.object({
    threads: z
        .array(
            z.object({
                personality: z.string(),
                personalityName: z.string().nullable(),
                items: z
                    .array(
                        z.object({
                            type: z.enum(["MESSAGE", "FUNCTION_CALL", "FUNCTION_CALL_OUTPUT", "REASONING"]),
                            role: z.enum(["USER", "ASSISTANT"]).nullable(),
                            content: z.string(),
                            toolName: z.string().nullable(),
                            toolArguments: z.string().nullable(),
                            toolCallId: z.string().nullable(),
                            reasoningId: z.string().nullable(),
                            reasoningSummary: z.array(z.string()),
                            encryptedReasoning: z.string().nullable(),
                            rating: z.union([z.literal("up"), z.literal("down")]).nullable(),
                        }),
                    )
                    .min(1, {
                        error: "Thread must have at least one item.",
                    }),
            }),
        )
        .min(1, {
            error: "You must provide at least one thread.",
        }),
});

export type IFormValues = z.infer<typeof schema>;

interface IProps {
    aiPersonality: {
        id: number;
        publicId: string;
        name: string;
    };
    handleSubmit: (values: IFormValues) => void;
    parsedData: IParsedInput;
}

export function buildInitialValuesFromParsedData(parsedData: IParsedInput): IFormValues {
    const threads: IFormValues["threads"] = [];
    for (const parsedThread of parsedData.threads) {
        if (parsedThread.ai_thread_items.length > 0) {
            threads.push({
                personality: parsedThread.personality,
                personalityName: parsedThread.personality_name ?? null,
                items: parsedThread.ai_thread_items.map((item) => ({
                    type: item.type,
                    role: item.role ?? null,
                    content: item.content,
                    toolName: item.tool_name ?? null,
                    toolArguments: item.tool_arguments ?? null,
                    toolCallId: item.tool_call_id ?? null,
                    reasoningId: item.reasoning_id ?? null,
                    reasoningSummary: item.reasoning_summary ?? [],
                    encryptedReasoning: item.encrypted_reasoning ?? null,
                    rating: item.rating ?? null,
                })),
            });
        }
    }
    return { threads };
}

function useInitialValues(parsedData: IParsedInput): IFormValues {
    return useMemo(() => buildInitialValuesFromParsedData(parsedData), [parsedData]);
}

function ImportSelectForm({ aiPersonality, handleSubmit, parsedData }: IProps): JSX.Element {
    const initialValues = useInitialValues(parsedData);

    const form = useForm<IFormValues>({
        initialValues,
        validate: zod4Resolver(schema) as any,
    });

    const removeOthers = () => {
        form.setFieldValue(
            "threads",
            form.getValues().threads.filter((thread) => thread.personality === aiPersonality.publicId),
        );
    };

    return (
        <form onSubmit={form.onSubmit(handleSubmit)}>
            <Group mb="md">
                <Button type="button" onClick={removeOthers} color="red">
                    Remove other personality threads
                </Button>
            </Group>
            <Accordion variant="contained">
                {form.getValues().threads.map((thread, index) => (
                    <Accordion.Item key={`${thread.personality}-${index}`} value={`${thread.personality}-${index}`}>
                        <Accordion.Control>{thread.personalityName ?? thread.personality}</Accordion.Control>
                        <Accordion.Panel>
                            <Group mb="md">
                                <Button type="button" onClick={() => form.removeListItem("threads", index)}>
                                    Remove
                                </Button>
                            </Group>
                            <Accordion classNames={classes} variant="contained">
                                {thread.items.map((item, itemIdx) => (
                                    <Accordion.Item
                                        key={`${thread.personality}-${index}-${itemIdx}`}
                                        value={`${thread.personality}-${index}-${itemIdx}`}
                                    >
                                        <Accordion.Control>
                                            {item.type}
                                            {item.role ? ` (${item.role})` : ""} #{itemIdx + 1}
                                        </Accordion.Control>
                                        <Accordion.Panel>
                                            <Stack py="md" gap="md">
                                                <Group>
                                                    <Button
                                                        type="button"
                                                        onClick={() =>
                                                            form.removeListItem(`threads.${index}.items`, itemIdx)
                                                        }
                                                    >
                                                        Remove
                                                    </Button>
                                                </Group>
                                                <InfoNode title="Type" order={4}>
                                                    <Text span>{item.type}</Text>
                                                </InfoNode>
                                                {item.role && (
                                                    <InfoNode title="Role" order={4}>
                                                        <Text span>{item.role}</Text>
                                                    </InfoNode>
                                                )}
                                                {item.rating && (
                                                    <InfoNode title="Rating" order={4}>
                                                        <Text span>{item.rating}</Text>
                                                    </InfoNode>
                                                )}
                                                <InfoNode title="Content" order={4}>
                                                    <Text className={classes.content}>{item.content}</Text>
                                                </InfoNode>
                                            </Stack>
                                        </Accordion.Panel>
                                    </Accordion.Item>
                                ))}
                            </Accordion>
                        </Accordion.Panel>
                    </Accordion.Item>
                ))}
            </Accordion>
            <Group justify="flex-end" mt="md">
                <Button type="button" onClick={() => form.reset()} color="red">
                    Reset
                </Button>
                <Button type="submit">Import</Button>
            </Group>
        </form>
    );
}

export default ImportSelectForm;
